/**
 * Exercise renderer protocol — typed answer union + grading router.
 *
 * The session host passes an ExerciseAnswer to gradeExercise and gets back a
 * GradingResult it can store without knowing which exercise type produced it.
 * Each renderer casts `detail` to the specific grading shape it understands.
 *
 * Platform split: these types live here (pure, no React). Each app maintains
 * its own renderer registry with platform-specific component implementations.
 *
 * See docs/design/exercise-renderer-protocol-v1.md §3.
 */

import type { Exercise } from "./exercise.js";
import type { FretPosition } from "@music-app/theory-engine";
import type { ScaleDegreeSequence } from "@music-app/theory-engine";
import type { FretboardGrading } from "./grade-fretboard.js";
import type { DictationGrading } from "./grade-dictation.js";
import type { RhythmicDictationGrading } from "./generators/rhythmic-dictation.js";
import { gradeAnswer } from "./generate.js";
import { gradeFretboard } from "./grade-fretboard.js";
import { gradeDictation } from "./grade-dictation.js";
import { gradeRhythmicGrid } from "./generators/rhythmic-dictation.js";

// ── Answer union ──────────────────────────────────────────────────────────────

export type ExerciseAnswer =
  | { type: "option";    optionId: string }
  | { type: "fretboard"; taps: FretPosition[] }
  | { type: "dictation"; sequence: ScaleDegreeSequence }
  | { type: "rhythmic";  grid: boolean[] };

// ── Grading result ────────────────────────────────────────────────────────────

export interface GradingResult {
  correct: boolean;
  explanation: string;
  /** Type-specific detail for feedback UI. The renderer casts this — the
   *  session never inspects it. */
  detail?: FretboardGrading | DictationGrading | RhythmicDictationGrading | null;
}

// ── Router ────────────────────────────────────────────────────────────────────

export function gradeExercise(exercise: Exercise, answer: ExerciseAnswer): GradingResult {
  switch (answer.type) {
    case "option": {
      const { correct, explanation } = gradeAnswer(exercise, answer.optionId);
      return { correct, explanation };
    }
    case "fretboard": {
      const grading = gradeFretboard(exercise, answer.taps);
      return { correct: grading.correct, explanation: grading.explanation, detail: grading };
    }
    case "dictation": {
      const grading = gradeDictation(exercise, answer.sequence);
      return { correct: grading.correct, explanation: grading.explanation, detail: grading };
    }
    case "rhythmic": {
      const pattern = exercise.meta.expectedPattern ?? "";
      const grading = gradeRhythmicGrid(pattern, answer.grid);
      const explanation = grading.correct
        ? "Correct."
        : `${grading.wrongCount} cell${grading.wrongCount === 1 ? "" : "s"} wrong.`;
      return { correct: grading.correct, explanation, detail: grading };
    }
    default: {
      // Exhaustive guard — unreachable under normal TypeScript usage.
      // Safe fallback for callers that bypass the type system.
      const _exhaustive: never = answer;
      void _exhaustive;
      return { correct: false, explanation: "" };
    }
  }
}
